import { TransactionTypesEnum, YesNoEmpty } from '@interfaces/models';
import { TransactionState } from '@store/index';
import { DecedentInfoState } from '@store/reducers/decedent-info/decedent-info.reducer';
import {
  FeatureToggleState,
  getMultiInheritanceToggle,
} from '@store/reducers/feature/feature.reducer';
import {
  getIsMultiInheritanceFlow,
  getIsTrusteeOrEstate,
  isIneligibleIRADesignation,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { VgEmploymentState } from 'vg-interaction-model';

export const getMultiInheritanceTriageNextPage = (
  triageState: TriageState,
  featureToggleState: FeatureToggleState,
): string => {
  const ineligible =
    !getIsMultiInheritanceFlow(triageState) || !getMultiInheritanceToggle(featureToggleState);
  const isTrusteeOrEstate = getIsTrusteeOrEstate(triageState) === YesNoEmpty.YES;
  if (ineligible) return 'error';
  if (isTrusteeOrEstate) return 'multi-inheritance/trust-triage';
  return 'multi-inheritance/summary';
};

export const getMultiInheritanceTrustTriageNextPage = (
  triageState: TriageState,
  featureToggleState: FeatureToggleState,
): string => {
  if (isIneligibleIRADesignation(triageState, featureToggleState)) {
    return 'inheritance/ineligible';
  } else {
    return 'multi-inheritance/summary';
  }
};

export const getMultiInheritanceDecedentInfoRoute = (
  decedentInfoState: DecedentInfoState,
): string => {
  if (decedentInfoState.isDeathVerified) {
    return 'secure/multi-inheritance/account-triage';
  } else {
    return 'secure/multi-inheritance/upload';
  }
};

export const getMultiInheritanceUploadNextPage = (): string => {
  return 'secure/multi-inheritance/account-triage';
};

export const getMultiInheritanceAccountTriageNextPage = (
  isNewAccount: boolean,
  isMultipleNewAccountTransaction: boolean,
): string => {
  if (!isNewAccount) {
    return 'secure/multi-inheritance/review';
  }
  return !isMultipleNewAccountTransaction
    ? 'secure/multi-inheritance/personal-info'
    : 'secure/multi-inheritance/investment-objectives';
};

export const getMultiInheritancePersonalInfoNextPage = (): string => {
  return 'secure/multi-inheritance/contact-info';
};

export const getMultiInheritanceContactInfoNextPage = (): string => {
  return 'secure/multi-inheritance/employment';
};

export const getMultiInheritanceEmploymentNextPage = (employment: VgEmploymentState): string => {
  const { associated, spouse } = employment.associations;
  const { employmentStatus } = employment.employmentStatus;

  if (associated && !spouse && employmentStatus !== 'VE') {
    return 'secure/multi-inheritance/upload/letter';
  }

  return 'secure/multi-inheritance/income-net-worth';
};

export const getMultiInheritanceLetterUploadNexPage = (): string => {
  return 'secure/multi-inheritance/income-net-worth';
};

export const getMultiInheritanceIncomeNetWorthNextPage = (): string => {
  return 'secure/multi-inheritance/investment-objectives';
};

export const getMultiInheritanceInvestObjNextPage = (): string => {
  return 'secure/multi-inheritance/new-account-review';
};

export const getMultiInheritanceNewAccountReviewNextPage = (
  currentTransactionId: string,
): string => {
  return !!currentTransactionId
    ? 'secure/multi-inheritance/review'
    : 'secure/multi-inheritance/account-triage';
};

export const getMultiInheritanceReviewNextPage = (transactions: TransactionState[]): string => {
  const isExistingSpouseFlow =
    !!transactions.length &&
    transactions.every(
      (transaction) =>
        transaction.selectedClientAccounts?.transactionType === TransactionTypesEnum.EXISTING,
    );

  if (isExistingSpouseFlow) {
    return 'secure/multi-inheritance/terms/existing';
  }

  return 'secure/multi-inheritance/terms/multi-new';
};

export const getMultiInheritanceTermsNextPage = (): string => {
  return 'secure/multi-inheritance/confirm';
};
